/**
 * Keep item_pool_memberships aligned with item_pools.priority_ids.
 *
 * The lists UI and engine both treat priority_ids as canonical membership.
 * The memberships join table is a durable audit trail (rank, selection_reason)
 * and must mirror priority_ids on every add/remove/reorder.
 */

import { getSupabaseClient } from './supabaseClient';
import { requireAuthUser } from './requireAuthUser';

export interface ReconcileItemPoolMembershipsResult {
  removed: number;
  upserted: number;
  error: string | null;
}

/**
 * Delete orphan membership rows and align ranks with priority_ids order.
 * Does not overwrite selection_reason / item_source on existing rows.
 */
export async function reconcileItemPoolMemberships(
  itemPoolId: string,
  priorityIds: string[],
  opts?: { actorId?: string | null },
): Promise<ReconcileItemPoolMembershipsResult> {
  const ids = priorityIds.filter(Boolean);
  const empty: ReconcileItemPoolMembershipsResult = { removed: 0, upserted: 0, error: null };

  try {
    const supabase = getSupabaseClient();

    const { data: existing, error: listErr } = await supabase
      .from('item_pool_memberships')
      .select('item_id')
      .eq('item_pool_id', itemPoolId);
    if (listErr) return { ...empty, error: listErr.message };

    const existingIds = new Set(
      (existing ?? []).map((r: { item_id: string }) => r.item_id).filter(Boolean),
    );

    let removed = 0;
    if (ids.length === 0) {
      if (existingIds.size > 0) {
        const { error: delErr } = await supabase
          .from('item_pool_memberships')
          .delete()
          .eq('item_pool_id', itemPoolId);
        if (delErr) return { ...empty, error: delErr.message };
        removed = existingIds.size;
      }
      return { removed, upserted: 0, error: null };
    }

    const canonical = new Set(ids);
    const orphanIds = [...existingIds].filter((itemId) => !canonical.has(itemId));
    if (orphanIds.length > 0) {
      const { error: delErr } = await supabase
        .from('item_pool_memberships')
        .delete()
        .eq('item_pool_id', itemPoolId)
        .in('item_id', orphanIds);
      if (delErr) return { ...empty, error: delErr.message };
      removed = orphanIds.length;
    }

    const now = new Date().toISOString();
    const missingIds = ids.filter((id) => !existingIds.has(id));

    if (missingIds.length > 0) {
      // added_by is NOT NULL in the DB — resolve the actor from the session
      // when the caller didn't pass one, and refuse a doomed insert otherwise.
      let actorId = opts?.actorId ?? null;
      if (!actorId) {
        const { user } = await requireAuthUser(supabase);
        actorId = user?.id ?? null;
      }
      if (!actorId) {
        return {
          removed,
          upserted: 0,
          error: 'Cannot insert pool memberships: no actorId and no authenticated session (added_by is required)',
        };
      }
      const insertRows = missingIds.map((itemId) => ({
        item_pool_id: itemPoolId,
        item_id: itemId,
        rank: ids.indexOf(itemId) + 1,
        added_at: now,
        added_by: actorId,
        item_source: 'sync' as const,
      }));
      const { error: insertErr } = await supabase.from('item_pool_memberships').insert(insertRows);
      if (insertErr) return { removed, upserted: 0, error: insertErr.message };
    }

    const rankUpdates = await Promise.all(
      ids.map((itemId, index) =>
        supabase
          .from('item_pool_memberships')
          .update({ rank: index + 1 })
          .eq('item_pool_id', itemPoolId)
          .eq('item_id', itemId),
      ),
    );
    const rankErr = rankUpdates.find((r) => r.error)?.error;
    if (rankErr) return { removed, upserted: missingIds.length, error: rankErr.message };

    return { removed, upserted: missingIds.length, error: null };
  } catch (e) {
    return {
      removed: 0,
      upserted: 0,
      error: e instanceof Error ? e.message : 'Failed to reconcile pool memberships',
    };
  }
}

/**
 * Load canonical member IDs for a pool (priority_ids), reconciling orphans first.
 * Falls back to memberships only when priority_ids is empty (legacy pools).
 */
export async function resolveCanonicalItemPoolMemberIds(
  itemPoolId: string,
): Promise<{ ids: string[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data: poolRow, error: poolErr } = await supabase
      .from('item_pools')
      .select('priority_ids')
      .eq('id', itemPoolId)
      .maybeSingle();
    if (poolErr) return { ids: [], error: poolErr.message };

    const fromArray = ((poolRow as { priority_ids?: string[] } | null)?.priority_ids ?? []).filter(
      Boolean,
    );

    if (fromArray.length > 0) {
      const reconcile = await reconcileItemPoolMemberships(itemPoolId, fromArray);
      if (reconcile.error) {
        console.warn('[resolveCanonicalItemPoolMemberIds] reconcile failed:', reconcile.error);
      } else if (reconcile.removed > 0) {
        console.info('[resolveCanonicalItemPoolMemberIds] removed orphan memberships', {
          itemPoolId,
          removed: reconcile.removed,
        });
      }
      return { ids: fromArray, error: null };
    }

    const { data: memRows, error: memErr } = await supabase
      .from('item_pool_memberships')
      .select('item_id, rank')
      .eq('item_pool_id', itemPoolId)
      .order('rank', { ascending: true });
    if (memErr) return { ids: [], error: memErr.message };

    const ids = ((memRows ?? []) as { item_id: string }[]).map((r) => r.item_id).filter(Boolean);
    return { ids, error: null };
  } catch (e) {
    return {
      ids: [],
      error: e instanceof Error ? e.message : 'Failed to resolve pool members',
    };
  }
}
